import type { ItemFieldRow } from './item-field-reader';
import { readInventoryField } from './inventory-raw-readers';
import { phpJsonString } from './php-json-values';
import { isPhpNumeric, phpJsonFloat, roundPhpDecimal } from './php-decimal-values';
import { readNodeItemId } from './layer-cell-readers';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import type { ProviderCatalogRows } from './provider-catalog-order';
import { createInventoryReadContext } from './inventory-read-context';
import type { InventoryReadContext } from './inventory-read-context';
import { createLayerValueReader } from './layer-value-reader';

export type InventorySizeCatalog = readonly Readonly<Record<string, unknown>>[];
const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const own = (value: unknown, key: string): unknown => arrayLike(value) && Object.prototype.hasOwnProperty.call(value, key)
    ? (value as Readonly<Record<string, unknown>>)[key] : undefined;
const validUuid = (value: string): boolean => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(value);
const asciiLower = (value: string): string => value.replace(/[A-Z]/g, character => character.toLowerCase());
function clone(value: unknown): unknown {
    if (!arrayLike(value)) return value ?? null;
    const entries = Object.entries(value);
    return entries.every(([key], index) => key === String(index)) ? entries.map(([, child]) => clone(child))
        : Object.fromEntries(entries.map(([key, child]) => [key, clone(child)]));
}
function writable(value: unknown): Record<string, unknown> {
    if (value == null || value === false) return {};
    if (!arrayLike(value)) throw new TypeError('Cannot write size layers to a scalar');
    return Object.fromEntries(Object.entries(value));
}
export function readSizeLadderIndex(input: unknown, catalog: InventorySizeCatalog): number {
    if (typeof input !== 'string' || input === '') return -1;
    const needle = asciiLower(input);
    return catalog.findIndex(row => asciiLower(phpJsonString(row.item_id ?? '')) === needle
        || asciiLower(phpJsonString(row.variable_name ?? '')) === needle);
}
export function readCarryingCapacityScale(from: unknown, to: unknown, catalog: InventorySizeCatalog): number {
    const a = readSizeLadderIndex(from, catalog), b = readSizeLadderIndex(to, catalog);
    return a < 0 || b < 0 || a === b ? 1 : 4 ** (b - a);
}
/** Exact contiguous size/capacity stage, before permissions. PHP's getById uses
 * exact-case UUID equality; its separate capacity ladder is case-insensitive.
 * sizeScale is the local value carried into subsequent weight/cost stages.
 */
export function compileInventorySize(target: ItemFieldRow, catalog: InventorySizeCatalog,
    layerValue: (row: ItemFieldRow, name: string) => unknown): { row: ItemFieldRow; sizeScale: number } {
    const out = writable(clone(target));
    let defaultSize = layerValue(out, 'size');
    if (arrayLike(defaultSize)) defaultSize = own(defaultSize, '0') ?? null;
    const override = readInventoryField(out, 'inventory_size');
    const validOverride = override !== null && override !== '' && validUuid(phpJsonString(override));
    const materialize = (name: string, value: unknown): void => {
        const layers = writable(out.item_layer_types); layers[name] = value; out.item_layer_types = layers;
    };
    if (validOverride) materialize('size', phpJsonString(override));
    out.size = layerValue(out, 'size') ?? catalog.find(row => row.variable_name === 'tiny')?.item_id ?? null;
    let sizeScale = 1;
    if (validOverride && typeof defaultSize === 'string' && validUuid(defaultSize)) {
        const base = (id: string): number => phpJsonFloat(catalog.find(row => row.item_id === id)?.base_size ?? 0);
        const instanceBase = base(phpJsonString(override)), templateBase = base(defaultSize);
        if (instanceBase > 0 && templateBase > 0) sizeScale = instanceBase / templateBase;
        let capacity = layerValue(out, 'carrying_capacity_pounds');
        if (arrayLike(capacity)) capacity = own(capacity, '0') ?? null;
        if (isPhpNumeric(capacity)) {
            const scale = readCarryingCapacityScale(defaultSize, phpJsonString(override), catalog);
            if (scale !== 1) materialize('carrying_capacity_pounds', roundPhpDecimal(phpJsonFloat(capacity) * scale, 2));
        }
    }
    return { row: clone(out) as ItemFieldRow, sizeScale };
}
export interface InventorySizeDependencies {
    catalogRows: ProviderCatalogRows;
    sizeCatalog: InventorySizeCatalog;
    resolveCarrierRef: (id: string) => ItemFieldRow | null;
    resolveInventoryInstance: (id: string) => ItemFieldRow | null;
    /** Supply the request guard when composing stages; the context owns resolver tracing. */
    inventoryContext?: InventoryReadContext;
    trace?: (event: readonly unknown[]) => void;
}
export function createInventorySizeCompiler(deps: InventorySizeDependencies): (target: ItemFieldRow) => { row: ItemFieldRow; sizeScale: number } {
    const emit = (event: readonly unknown[]): void => { deps.trace?.(event); };
    const nodeItemId = (row: ItemFieldRow): string | null => { const id = readNodeItemId(row); emit(['node', id]); return id; };
    const provider = createCatalogDefaultProviderReader({ catalogRows: deps.catalogRows, nodeItemId,
        resolveCarrierRef: id => { emit(['reference', id]); return deps.resolveCarrierRef(id); } });
    const inventoryContext = deps.inventoryContext ?? createInventoryReadContext(id => { emit(['inventory', id]); return deps.resolveInventoryInstance(id); });
    const providers = function* (row: ItemFieldRow): Generator<ItemFieldRow> { emit(['providers']); yield* provider.providers(row); };
    const scalar = createLayerValueReader({ nodeItemId, inventoryContext, providers });
    return target => compileInventorySize(target, deps.sizeCatalog, scalar.read);
}
